import {
  Button,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material'
import numeral from 'numeral'
import { useMemo } from 'react'

import {
  ItemResaleRaffleUserDetailFragment,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout } from '@/components/layouts'
import { useRouter, useTranslation } from '@/core'
import { OrganismFrame } from '@/core/architecture'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { getItemSkuPrice, setItemSku } from '@/core/sku'

export * from './fragments'

export type State = {
  itemResaleRaffleUser: Maybe<ItemResaleRaffleUserDetailFragment> | undefined
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const ResaleRaffleRepaymentCompletionTemplate: Template<State, Action> =
  ({ state }) => {
    const { t } = useTranslation('resaleRaffleRepaymentCompletion')
    const { routerPages, generatePath } = useRouter()

    return (
      <BaseLayout margin header={{ title: t('title') }}>
        <ResultCell />
        {state.itemResaleRaffleUser && (
          <DetailCell itemResaleRaffleUser={state.itemResaleRaffleUser} />
        )}
        <Stack alignItems="center" spacing={4} sx={{ pt: '44px', pb: 6 }}>
          <Button
            size="large"
            href={generatePath(routerPages.ResaleManagement, {
              tab: 'purchased',
            })}
            sx={{ width: 244, margin: 'auto' }}
            color="primary"
          >
            {t('resaleManagementScreen')}
          </Button>
          <Button
            variant="text"
            href={routerPages.Top}
            size="large"
            sx={{ width: 244, margin: 'auto' }}
            color="primary"
          >
            {t('topPage')}
          </Button>
        </Stack>
      </BaseLayout>
    )
  }

const ResultCell: Cell = () => {
  const { t } = useTranslation('resaleRaffleRepaymentCompletion')
  return (
    <CellFrame sx={{ py: 7 }}>
      <Stack spacing={6} alignItems="center">
        <Typography variant="h6">{t('purchaseCompleted')}</Typography>
        <Typography variant="subtitle1" color="text.secondary" align="center">
          {t('purchaseCompletedDesc')}
        </Typography>
      </Stack>
    </CellFrame>
  )
}

const DetailCell = ({
  itemResaleRaffleUser,
}: {
  itemResaleRaffleUser: State['itemResaleRaffleUser']
}) => {
  const { t } = useTranslation('resaleRaffleRepaymentCompletion')
  const { formattedPrice } = useMemo(
    () => getItemSkuPrice(itemResaleRaffleUser?.item),
    [itemResaleRaffleUser]
  )

  const data = [
    {
      label: t('ticketPrice'),
      value: `¥${formattedPrice}/1${t('perSheet')}`,
    },
    {
      label: t('applicationNumber'),
      value: t('applicationNumberValue', {
        number: itemResaleRaffleUser?.itemCount,
      }),
    },
    {
      label: t('totalAmount'),
      value: `¥${numeral(
        (itemResaleRaffleUser?.totalItemPrice || 0) +
          (itemResaleRaffleUser?.item?.metadata?.ticket?.resaleFee
            ?.purchaseFee || 0)
      ).format('0,0')}`,
    },
    {
      label: t('paymentInfo'),
      value: t('creditCard'),
    },
    {
      label: t('applicationDate'),
      value: itemResaleRaffleUser?.formattedCreatedAt,
    },
  ]

  const skuName = useMemo(() => {
    return (
      setItemSku(
        itemResaleRaffleUser?.item,
        itemResaleRaffleUser?.skuId ?? null
      )?.sku?.unitName ?? ''
    )
  }, [itemResaleRaffleUser])
  return (
    <OrganismFrame>
      <Card elevation={0}>
        <CardContent sx={{ p: 5, '&:last-child': { pb: 5 } }}>
          <Stack spacing={5}>
            <Stack spacing={1}>
              <Typography variant="h5">
                {itemResaleRaffleUser?.item.title}
              </Typography>
              {!!skuName && (
                <Typography variant="subtitle1">{skuName}</Typography>
              )}
            </Stack>

            <Divider />
            <Stack spacing={6}>
              {data.map(({ label, value }, index) => (
                <Stack key={index} spacing={1}>
                  <Typography variant="subtitle1" color="text.secondary">
                    {label}
                  </Typography>
                  <Typography variant="body2">{value}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </OrganismFrame>
  )
}
